/* eslint-disable valid-jsdoc */
//@ts-check
import moment from 'moment';
import {
  assoc,
  any,
  chain,
  compose,
  filter,
  find,
  ifElse,
  isEmpty,
  isNil,
  juxt,
  map,
  omit,
  pathOr,
  pick,
  pipe,
  prop,
  propOr,
  props,
  propEq,
  reject,
  when,
  always,
} from 'ramda';

import {
  getActiveFilters,
  getDateObj,
  getPackageTypeFilterValue,
  getSortColumn,
  getSortDirection,
  getStationFilterValue,
  getStatusFilterValue,
} from './selectors';

export const SEARCH_RESULT_LIMIT = 1000;

const omitBlacklist = omit(['name', 'text', 'selected', 'exclusive', 'basicFilter']);
const filterNil = reject(isNil);
const findSelected = find(propEq('selected', true));
const whereSelected = filter(propEq('selected', true));

const conditionValidFields = ['field', 'operation', 'value'];
const criterionIsInvalid = pipe(props(conditionValidFields), any(isNil));

const conditionNestedValidFields = ['expressions', 'operation'];
const criterionNestedIsInvalid = pipe(props(conditionNestedValidFields), any(isNil));


/**
 * takes some object and checks if it is leaf invalid or nested invalid.  leaf and nested check return true
 * when input is not a valid criterion type.
 * both are never going to be false at one time (cannot be valid for both)
 * leaf | nest | AND (result)
 * -----+------+-------------
 *  F   | F    | F (valid) * impossible state
 *  F   | T    | F (valid)
 *  T   | F    | F (valid)
 *  T   | T    | T (invalid)
 * @param {Object} input - the criterion object to validate
 * @returns {Boolean} True: the object was invalid, should not be used, False: the object was valid for one type.
 */
const nestedOrLeafCriterionIsInvalid = input => criterionIsInvalid(input) && criterionNestedIsInvalid(input);

/**
 * A Filter Item definition
 * @typedef {Object} FilterItem
 * @prop {string} [name] - unique key
 * @prop {string} [text] - the text displayed
 * @prop {string} [field] - cloud search field to match or take action
 * @prop {Operation} operation - one of the cloud search operations expects 'GT', 'BETWEEN', 'EQUAL', 'LT', 'NOT'
 * @prop {string | number} value - the to match value
 * @prop {boolean} [selected] - determine if the item is currently selected
 * @prop {boolean} [exclusive] - when selected, deselects all other items in the group
 * @prop {boolean} [basicFilter] - Used to determine if the filter appears in the quick filters
 * @prop {string} [sortColumn] - Cloud Search field to sort the results on
 * @prop {string} [sortDirection] - the direction to sort the results, expects 'ascending' or 'descending'
 */

/**
 * General filter definition object
 * @typedef {Object} FilterDefinition
 * @prop {string} groupName - used for heading in advanced filters
 * @prop {string} filterType - expected values 'checkbox' or 'radio'
 * @prop {number} basicFilterGroupPosition - positioning weight for where to put the basic filter group
 * @prop {Array.<FilterItem>} filters - A list of filter items
 */

/**
 * @typedef {object} SearchText
 * @prop {string} text
 * @prop {string} [placeholderText]
 * @prop {string} [filterType]
 * @prop {string} groupName
 * @prop {FilterItem[]} filters
 */

/**
 * A search object
 * @typedef {Object} Query
 * @prop {SearchText} searchText - Text used in the input text search
 * @prop {string|Date} fromDate
 * @prop {string|Date} thruDate
 * @prop {FilterDefinition} [packageType] - the filter definition Object
 * @prop {FilterDefinition} [fields] - Fields filter for by 'field' type, applies to search ?
 * @prop {FilterDefinition} [status] - shortcut for handling filters by package status
 * @prop {FilterDefinition} [sortOptions] - defines how sorting will occur
 */

/**
 * @param {Query} query
 * @returns {SearchParams}
 */
export const getSearchParametersV1 = (query) => {
  const searchText = [query.searchText.text];
  const typeFilter = getPackageTypeFilterValue(query);
  const stationFilter = getStationFilterValue(query);
  const sort = getSortDirection(query) + getSortColumn(query);
  const dates = getDateObj(query);
  const statuses = getStatusFilterValue(query);
  const searchFields = getActiveFilters(pathOr([], ['field', 'filters'], query));

  // If no dateThru is set, CloudSearch will apply a default date range
  if (!dates.dateThru) {
    dates.dateThru = moment().utc().toISOString();
  }

  return {
    offset: 0,
    count: SEARCH_RESULT_LIMIT,
    orderBy: sort,
    commonAndClause: {
      type: typeFilter,
      property: stationFilter,
      dateFrom: dates.dateFrom,
      dateThru: dates.dateThru,
      textMatches: searchText,
      indexSearchTerms: {...statuses},
    },
    orClauses: searchFields.map(searchField => ({
      andClauses: [{
        indexSearchTerms: {
          [searchField]: searchText,
        },
      }],
    })),
  };
};

export const getSearchType = (query) => {
  const {searchText: searchTextFilter} = query;
  const trimmedText = searchTextFilter.text.trim();
  const useExactSearch = trimmedText.startsWith('"') && trimmedText.endsWith('"');
  return useExactSearch ? 'WILD' : searchTextFilter.searchType;
};

/**
 * @typedef {'OR' | 'AND' | 'LT' | 'GT' | 'TERM' | 'RANGE' | 'BEFORE' | 'AFTER' | 'BETWEEN' | 'EQUAL' | 'NOT' | 'EXACT'} Operation
 */

/**
 * @typedef {object} Expression
 * @prop {string} [field]
 * @prop {Operation} operation
 * @prop {Expression[]} [expressions]
 * @prop {string} [value]
 * @prop {string} [after]
 * @prop {string} [before]
 */

/**
 * Takes the string to search for in cloud search
 * @param {string} searchText
 * @param {Operation} operation
 * @returns {{operation: string, expressions: Expression[]}}
 */
const buildTextExpression = (searchText, operation) => (
  {
    operation: 'OR',
    expressions: [
      {
        field: 'title_text',
        operation,
        value: searchText,
      },
      {
        field: 'name_text',
        operation,
        value: searchText,
      },
      {
        field: 'summary_text',
        operation,
        value: searchText,
      },
    ],
  });

/**
 * build a query that allows searchText to be targeted to a specific field, such as title or summary
 * @param {Query} param0 provides the field parameter and searchText parameter
 * @returns {{operation?: string, searchType: string, expressions?: Expression[]} | {}}
 */
export const searchTextExpression = (query) => {
  const {field: {filters = []} = {}, searchText: searchTextFilter} = query;

  if (!searchTextFilter || isEmpty(searchTextFilter)) {
    return {};
  }

  const trimmedText = searchTextFilter.text.trim();
  const useExactSearch = trimmedText.startsWith('"') && trimmedText.endsWith('"');
  const searchText = useExactSearch ? trimmedText.substring(1, trimmedText.length - 1) : trimmedText;

  const selectedFilters = whereSelected([...filters, ...searchTextFilter.filters]);
  if (selectedFilters.length > 0 && selectedFilters[0].field) {
    const expressions = selectedFilters.map(filter => ({
      field: filter.field,
      operation: useExactSearch ? 'EXACT' : filter.operation,
      value: searchText,
    }));

    if (expressions.length >= 1) {
      return ({operation: 'OR', expressions});
    }
  }
  return buildTextExpression(searchText, useExactSearch ? 'EXACT' : 'TERM');
};

/**
 * @param {{fromDate: Date, thruDate: Date, dateRangeType: FilterDefinition}} param0
 * @returns {Expression | {}}
 */
export const dateExpression = ({fromDate, thruDate, dateRangeType}) => {
  if (!(fromDate && thruDate)) {
    return {};
  }

  const dateField = dateRangeType?.filters.find(filter => filter.selected)?.sortColumn || 'last_saved_date';

  return fromDate
    ? {
      field: dateField,
      operation: 'RANGE',
      after: moment(fromDate).toISOString(),
      before: moment(thruDate).endOf('day').toISOString(),
    }
    : {
      field: dateField,
      operation: 'BEFORE',
      before: moment(thruDate).endOf('day').toISOString(),
    };
};

/**
 * @param {string} packageType
 * @returns {Expression}
 */
const packageFilter = packageType => ({
  field: 'package_type_id',
  operation: 'TERM',
  value: packageType,
});

/**
 * @param {string[]} types
 * @returns {Expression}
 */
export const packageTypeExpression = (types = ['OTTEpisode', 'OTTSeason', 'OTTShow']) => ({
  operation: 'OR',
  expressions: map(packageFilter)(types),
});

const queryToPackageTypeExpression = pipe(
  pathOr([], ['packageType', 'filters']),
  filter(propEq('selected', true)),
  chain(prop('value')),
  ifElse(isEmpty, always({}), packageTypeExpression),
);

/**
 * @param {object[]} values
 * @returns {Expression}
 */
const tagTypeExpression = (values) => ({
  field: 'content_tags',
  operation: 'TERM',
  value: Array.isArray(values) ? values[0] : values,
});

const queryToSourceExpression = pipe(
  pathOr([], ['source', 'filters']),
  filter(propEq('selected', true)),
  chain(prop('value')),
  ifElse(isEmpty, always({}), tagTypeExpression),
);

const queryToNetworkExpression = pipe(
  pathOr([], ['network', 'filters']),
  filter(propEq('selected', true)),
  chain(prop('value')),
  ifElse(isEmpty, always({}), tagTypeExpression),
);

const multiTagExpression = uuid => ({
  operation: 'AND',
  expressions: map(tagTypeExpression)(uuid),
});

const queryToTagExpression = pipe(
  pathOr([], ['tag', 'filters']),
  filter(propEq('selected', true)),
  chain(prop('value')),
  filterNil,
  ifElse(isEmpty, always({}), multiTagExpression),
);

const unpackValueIfArray = when(v => Array.isArray(v.value), v => assoc('value', v.value[0], v));

/**
 * @param {Expression[]} expressions
 * @returns {Expression[]}
 */
const extractCriterionFields = expressions => {
  const isSelectedAndIsValidFilter = exp => propEq('selected', true, exp) && !nestedOrLeafCriterionIsInvalid(exp);
  return expressions.filter(isSelectedAndIsValidFilter)
    .map(
      exp => exp.expressions
        ? pick(conditionNestedValidFields, exp)
        : pipe(pick(conditionValidFields), unpackValueIfArray)(exp)
    );
};

const ifArrayGetLength = arr => Array.isArray(arr) ? arr.length : arr;

/**
 * take a list of criterion and an optional operation string to generate a generic cloud search query
 * @param {Array.<Expression>} _filters - search type object to generate a cloud search query
 * @param {String} [_operation] - the operation for the current level of query, defaults to 'OR'
 * @returns {Object|Array.<Expression>} a criterion object or a list of criterion objects.
 */
export const genericExpression = (_filters, _operation = 'OR') => {
  if (!ifArrayGetLength(_filters)) {
    return {};
  }

  const _expressions = extractCriterionFields(_filters).map(
    when(criterionIsInvalid, _exp => assoc('expressions', genericExpression(_exp.expressions, _exp.operation), _exp))
  );

  return ifArrayGetLength(_expressions) === 1 ? _expressions[0] : ({operation: _operation, expressions: _expressions});
};

const toGenericExpression = pipe(whereSelected, reject(nestedOrLeafCriterionIsInvalid), genericExpression);

/**
 * @param {string} pathName
 * @returns {function({[k: string]: FilterDefinition}): Expression}
 */
const queryToGenericExpression = (pathName) => pipe(
  pathOr([], [pathName, 'filters']),
  toGenericExpression
);


/**
 * @param {{minDuration?: string, maxDuration?: string}} expression
 * @returns {Expression}
 */
export const durationExpression = (expression) => {
  const {minDuration, maxDuration} = expression;
  const field = 'duration_size';
  if (minDuration && maxDuration) {
    return {
      after: minDuration,
      before: maxDuration,
      field,
      operation: 'BETWEEN',
    };
  }

  if (minDuration) {
    return {
      field: 'duration_size',
      operation: 'LT',
      value: minDuration,
    };
  }

  if (maxDuration) {
    return {
      field: 'duration_size',
      operation: 'GT',
      value: maxDuration,
    };
  }

  return expression;
};

export const queryToDurationExpression = ({field: {filters = []} = {}} = {}) => {
  if (!Array.isArray(filters) || !filters.length) {
    return {};
  }
  const selectedFilter = filters.find(propEq('selected', true));
  return compose(
    when(criterionIsInvalid, () => ({})),
    omitBlacklist,
    filterNil
  )(selectedFilter);
};

export const queryToSimpleConstraintsExpression = ifElse(
  pathOr(false, ['constraint', 'filters']),
  pipe(
    pathOr([], ['constraint', 'filters']),
    findSelected,
    filterNil,
    omitBlacklist
  ),
  () => ({})
);

const sortDirections = {
  ascending: 'asc',
  descending: 'desc',
};

// orderByExpression :: Query SearchParams -> Expression
export const orderByExpression = ({sortColumn = 'last_saved_date', sortDirection = 'descending'}) =>
  `${sortColumn} ${sortDirections[sortDirection]}`;

const queryToOrderBy = ({sortOptions: {filters}}) =>
  orderByExpression(filters.find(propEq('selected', true)));

// buildV2Parameters :: SearchParams -> SearchParams
export const buildV2Parameters = ({
  domain = 'STORYLINE',
  expressions,
  orderBy,
  offset,
  limit,
  query,
}) => ({
  domain,
  searchType: getSearchType(query),
  operation: 'AND',
  expressions: reject(isEmpty, expressions),
  orderBy,
  offset,
  limit,
});

// getSearchParametersV2 :: (Query, number, number, {k:v}) -> SearchParams
export const getSearchParametersV2 = (query, offset, limit, options) =>
  buildV2Parameters({
    expressions: juxt([
      propOr({}, 'expression'),
      searchTextExpression,
      dateExpression,
      queryToPackageTypeExpression,
      queryToSourceExpression,
      queryToNetworkExpression,
      queryToTagExpression,
      queryToDurationExpression,
      queryToSimpleConstraintsExpression,
      queryToGenericExpression('generic'),
      queryToGenericExpression('status'),
      queryToGenericExpression('tag'),
      queryToGenericExpression('checkbox'),
    ])(query),
    orderBy: queryToOrderBy(query),
    limit,
    offset,
    query,
    ...options,
  });
